import React, { useState, useEffect } from 'react';
import { Search, FileText, Package, TrendingUp, AlertCircle, Eye, X, ArrowUpDown, ArrowUp, ArrowDown, Filter, Clock, Upload, RotateCcw, Printer, Download } from 'lucide-react';
import API_BASE_URL from "../config";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import megalogo from '../Image/megalogo.png';
import * as XLSX from 'xlsx';

const PR_Reports = () => {
  const [prHeaders, setPrHeaders] = useState([]);
  const [filteredPRs, setFilteredPRs] = useState([]);
  const [selectedPR, setSelectedPR] = useState(null);
  const [prDetails, setPrDetails] = useState([]);
  const [prAttachments, setPrAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [sortField, setSortField] = useState('prNo');
  const [sortDirection, setSortDirection] = useState('asc');

  const [secondApprovals, setSecondApprovals] = useState([]);
  const fetchSecondApprovals = async (prHeaderId) => {
    try {
      const res = await fetch(`${API_BASE_URL}api/SecondApproverPR`);
      if (!res.ok) throw new Error("Failed to fetch second approvals");
      const data = await res.json();
      const prSecondApprovals = data.filter(
        a => a.prHeaderId === prHeaderId && a.response === 1
      );
      setSecondApprovals(prSecondApprovals);
    } catch (err) {
      console.error("Second approval error:", err);
    }
  };

  const [companies, setCompanies] = useState({});
  const [expenseTypes, setExpenseTypes] = useState({});
  const [paymentMethods, setPaymentMethods] = useState({});
  const [locations, setLocations] = useState({});
  const [departments, setDepartments] = useState({});
  const [statusFilter, setStatusFilter] = useState("all");
  const [destinations, setDestinations] = useState({});

  const fetchDestinations = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/InventoryDestination`);
      const data = await res.json();
      const destMap = {};
      data.forEach((d) => { destMap[d.destinationId] = d; });
      setDestinations(destMap);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchCompanies = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/Company`);
      if (!res.ok) throw new Error("Failed to fetch companies");
      const data = await res.json();
      const companyMap = {};
      data.forEach((c) => { companyMap[c.id] = c.companyName || c.CompanyName || "Unknown"; });
      setCompanies(companyMap);
    } catch (error) { console.error(error); }
  };

  const fetchExpenseTypes = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/ExpenseType`);
      if (!res.ok) throw new Error("Failed to fetch expense types");
      const data = await res.json();
      const expenseMap = {};
      data.forEach((e) => { expenseMap[e.id] = e.expenseTypeName || e.ExpenseTypeName || "Unknown"; });
      setExpenseTypes(expenseMap);
    } catch (error) { console.error(error); }
  };

  const fetchPaymentMethods = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/M_Payment_Method`);
      const data = await res.json();
      const paymentMap = {};
      data.forEach((p) => { paymentMap[p.id] = p.paymentMethodName || "Unknown"; });
      setPaymentMethods(paymentMap);
    } catch (error) { console.error(error); }
  };

  const fetchLocations = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/Location`);
      const data = await res.json();
      const locationMap = {};
      data.forEach((l) => { locationMap[l.id] = l.locationName || "Unknown"; });
      setLocations(locationMap);
    } catch (error) { console.error(error); }
  };

  const fetchDepartments = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/Department`);
      const data = await res.json();
      const deptMap = {};
      data.forEach((d) => { deptMap[d.id] = d.departmentName || "Unknown"; });
      setDepartments(deptMap);
    } catch (error) { console.error(error); }
  };

  useEffect(() => {
    fetchCompanies();
    fetchExpenseTypes();
    fetchPaymentMethods();
    fetchLocations();
    fetchDepartments();
    fetchDestinations();
  }, []);

  const [websiteInfo, setWebsiteInfo] = useState(null);
  const [customLogo, setCustomLogo] = useState(null);
  const [showLogoUploader, setShowLogoUploader] = useState(false);
  const [approverPositions, setApproverPositions] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    fetchPRHeaders();
    fetchMasterData();
  }, []);

  useEffect(() => {
    filterAndSortPRs();
  }, [searchTerm, statusFilter, prHeaders, sortField, sortDirection]);

  const fetchMasterData = async () => {
    try {
      const [companiesRes, expenseTypesRes, paymentMethodsRes, locationsRes, departmentsRes] = await Promise.all([
        fetch(`${API_BASE_URL}api/M_Company`),
        fetch(`${API_BASE_URL}api/M_Expense_Type`),
        fetch(`${API_BASE_URL}api/M_Payment_Method`),
        fetch(`${API_BASE_URL}api/M_Location`),
        fetch(`${API_BASE_URL}api/M_Department`)
      ]);
      const companiesData = await companiesRes.json();
      const expenseTypesData = await expenseTypesRes.json();
      const paymentMethodsData = await paymentMethodsRes.json();
      const locationsData = await locationsRes.json();
      const departmentsData = await departmentsRes.json();
      setCompanies(Object.fromEntries(companiesData.map(c => [c.id, c.companyName])));
      setExpenseTypes(Object.fromEntries(expenseTypesData.map(e => [e.id, e.expenseType])));
      setPaymentMethods(Object.fromEntries(paymentMethodsData.map(p => [p.id, p.paymentMethod])));
      setLocations(Object.fromEntries(locationsData.map(l => [l.id, l.locationName])));
      setDepartments(Object.fromEntries(departmentsData.map(d => [d.id, d.departmentName])));
    } catch (err) {
      console.error('Error fetching master data:', err);
    }
  };

  const fetchPRHeaders = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}api/PR_Header`);
      if (!response.ok) throw new Error("Failed to fetch PR headers");
      const data = await response.json();

      const user = JSON.parse(localStorage.getItem("user"));
      if (!user) { setPrHeaders([]); setFilteredPRs([]); return; }

      const levelRes = await fetch(`${API_BASE_URL}api/UserLevels`);
      const levels = await levelRes.json();
      const userLevel = levels.find(l => l.isActive === 1 && l.userLevelName === user.positionId);

      if (!userLevel) { setPrHeaders([]); setFilteredPRs([]); return; }

      let filteredData = [];
      if (userLevel.accessViewAllData === true) {
        filteredData = data;
      } else if (userLevel.accessVIewUserOnly === true) {
        filteredData = data.filter(pr => pr.createdBy === user.userId);
      }
      setPrHeaders(filteredData);
      setFilteredPRs(filteredData);
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchPRDetails = async (prHeaderId) => {
    try {
      setDetailsLoading(true);
      const response = await fetch(`${API_BASE_URL}api/PR_Details/PRHeader/${prHeaderId}`);
      if (!response.ok) throw new Error('Failed to fetch PR details');
      const data = await response.json();
      setPrDetails(data);
    } catch (err) {
      setError(err.message);
      setPrDetails([]);
    } finally {
      setDetailsLoading(false);
    }
  };

  const fetchPRAttachments = async (prHeaderId) => {
    try {
      const response = await fetch(`${API_BASE_URL}api/PR_Attachments/PRHeader/${prHeaderId}`);
      if (!response.ok) throw new Error('Failed to fetch attachments');
      const data = await response.json();
      setPrAttachments(data);
    } catch (err) {
      console.error('Error fetching attachments:', err);
      setPrAttachments([]);
    }
  };

  const fetchWebsiteInfo = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/Website_Details`);
      if (!res.ok) throw new Error('Failed to fetch website info');
      const data = await res.json();
      const activeWebsite = data.find(w => w.isActive) || data[0];
      if (activeWebsite) setWebsiteInfo(activeWebsite);
    } catch (error) {
      console.error('Error fetching website info:', error);
    }
  };

  const fetchApprovals = async (prHeaderId) => {
    try {
      const res = await fetch(`${API_BASE_URL}api/T_PR_Approval`);
      if (!res.ok) throw new Error("Failed to fetch approvals");
      const data = await res.json();
      const prApprovals = data.filter(
        a => a.prHeaderId === prHeaderId && a.response === 1 && a.isActive === 1
      );
      setApprovals(prApprovals);
    } catch (err) {
      console.error("PR Approval error:", err);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/Users`);
      if (!res.ok) throw new Error("Failed to fetch users");
      const data = await res.json();
      setUsers(data.filter(u => u.isActive === 1));
    } catch (err) {
      console.error("Users error:", err);
    }
  };

  const fetchApproverPositions = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/M_Approver_Position`);
      if (!res.ok) throw new Error("Failed to fetch approver positions");
      const data = await res.json();
      const prApprovers = data.filter(a => a.pr === 1).sort((a, b) => a.colum - b.colum);
      setApproverPositions(prApprovers);
    } catch (err) {
      console.error("Approver Position error:", err);
    }
  };

  const filterAndSortPRs = () => {
    let filtered = [...prHeaders];
    if (searchTerm) {
      filtered = filtered.filter(pr =>
        pr.prNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (pr.remarks && pr.remarks.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    if (statusFilter !== 'all') {
      filtered = filtered.filter(pr => pr.status.toString() === statusFilter);
    }
    filtered.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      if (sortField === 'creationDate' || sortField === 'dateNeeded') {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      return sortDirection === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
    });
    setFilteredPRs(filtered);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <ArrowUpDown className="w-4 h-4 opacity-50" />;
    return sortDirection === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />;
  };

  const handleViewDetails = async (pr) => {
    setSelectedPR(pr);
    setShowModal(true);
    await Promise.all([
      fetchPRDetails(pr.id),
      fetchPRAttachments(pr.id),
      fetchWebsiteInfo(),
      fetchApprovals(pr.id),
      fetchUsers(),
      fetchApproverPositions(),
      fetchSecondApprovals(pr.id),
      fetchRequestors()
    ]);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedPR(null);
    setPrDetails([]);
    setPrAttachments([]);
    setCustomLogo(null);
    setApprovals([]);
    setSecondApprovals([]);
    setApproverPositions([]);
    setUsers([]);
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { alert('Please upload an image file'); return; }
    if (file.size > 2 * 1024 * 1024) { alert('File size must be less than 2MB'); return; }
    const reader = new FileReader();
    reader.onloadend = () => { setCustomLogo(reader.result); setShowLogoUploader(false); };
    reader.readAsDataURL(file);
  };

  const handleResetLogo = () => { setCustomLogo(null); setShowLogoUploader(false); };
  const getLogoSrc = () => customLogo || megalogo;

  // ── APPROVED USER (with cascade) ──────────────────────────────────
  const getApprovedUser = (approverUserLevelId) => {
    // 1. May sariling approval?
    const approval = approvals.find(a => a.approverUserLevelId === approverUserLevelId);
    if (approval) {
      const user = users.find(u => u.id === approval.actualApproverId);
      const signatureUrl = approval.actualApproverId
        ? `${API_BASE_URL}api/Users/${approval.actualApproverId}/digitalsign`
        : null;
      return {
        name: approval.actualApproverName || (user ? `${user.firstName} ${user.lastName}` : "—"),
        date: approval.responseDate,
        signatureUrl
      };
    }

    // 2. Auto-fill — walang sariling approval, may lower approval ba?
    const currentPosition = approverPositions.find(p => p.approverUserLevelId === approverUserLevelId);
    if (!currentPosition) return null;

    const lowerPositions = approverPositions.filter(p => p.colum < currentPosition.colum);
    if (lowerPositions.length === 0) return null;

    const hasAnyLowerApproval = lowerPositions.some(pos =>
      approvals.find(a => a.approverUserLevelId === pos.approverUserLevelId)
    );
    if (!hasAnyLowerApproval) return null;

    // Kunin sa users yung may matching userLevelId — hindi mag-duplicate
    const matchedUser = users.find(u => u.userLevelId === approverUserLevelId);
    if (!matchedUser) return null;

    return {
      name: `${matchedUser.firstName} ${matchedUser.lastName}`.trim(),
      date: null,
      signatureUrl: `${API_BASE_URL}api/Users/${matchedUser.id}/digitalsign`
    };
  };

  // ── SECOND APPROVER ───────────────────────────────────────────────
  const getSecondApprover = (approvalLevel) => {
    const approval = secondApprovals.find(a => a.approvalLevel === approvalLevel);
    if (!approval) return null;
    const signatureUrl = approval.actualApproverId
      ? `${API_BASE_URL}api/Users/${approval.actualApproverId}/digitalsign`
      : null;
    return { name: approval.actualApproverName || "—", date: approval.responseDate, signatureUrl };
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  // ── EXPORT TO EXCEL ───────────────────────────────────────────────
  const handleExportToExcel = () => {
    const statusLabels = { 0: 'Draft', 1: 'Pending', 2: 'Approved', 3: 'Rejected', 4: 'Cancelled' };

    const exportData = filteredPRs.map((pr, idx) => ({
      '#': idx + 1,
      'PR Number': pr.prNo || '—',
      'Control Number': pr.controlNumber || '—',
      'Requestor': pr.requestor || '—',
      'Department': departments[pr.chargedToDept] || '—',
      'Company': companies[pr.companyId] || '—',
      'Expense Type': expenseTypes[pr.expenseTypeId] || '—',
      'Payment Method': paymentMethods[pr.paymentMethodId] || '—',
      'Location': locations[pr.locationId] || '—',
      'Priority': pr.priority || '—',
      'Date Created': pr.creationDate ? new Date(pr.creationDate).toLocaleDateString('en-US') : '—',
      'Date Needed': pr.dateNeeded ? new Date(pr.dateNeeded).toLocaleDateString('en-US') : '—',
      'Date Submitted': pr.dateSubmittedForApproval ? new Date(pr.dateSubmittedForApproval).toLocaleDateString('en-US') : '—',
      'Status': statusLabels[pr.status] || 'Unknown',
      'Remarks': pr.remarks || '—',
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);

    // Auto column width
    const colWidths = Object.keys(exportData[0] || {}).map(key => ({
      wch: Math.max(key.length, ...exportData.map(row => String(row[key] || '').length)) + 2
    }));
    worksheet['!cols'] = colWidths;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'PR Reports');
    XLSX.writeFile(workbook, `PR_Reports_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // ── EXPORT TO PDF ─────────────────────────────────────────────────
  const handleExportToPDF = async () => {
    const element = document.getElementById("pr-pdf-content");
    const canvas = await html2canvas(element, {
      scale: 2, useCORS: true, backgroundColor: "#ffffff",
      windowHeight: element.scrollHeight, height: element.scrollHeight
    });
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const imgHeight = (canvas.height * pdfWidth) / canvas.width;
    let heightLeft = imgHeight;
    let position = 0;
    pdf.addImage(imgData, "PNG", 0, position, pdfWidth, imgHeight);
    heightLeft -= pdfHeight;
    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, "PNG", 0, position, pdfWidth, imgHeight);
      heightLeft -= pdfHeight;
    }
    pdf.save(`PR-${selectedPR.prNo}.pdf`);
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      0: { label: 'Draft', color: 'bg-gray-100 text-gray-800' },
      1: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
      2: { label: 'Approved', color: 'bg-green-100 text-green-800' },
      3: { label: 'Rejected', color: 'bg-red-100 text-red-800' },
      4: { label: 'Cancelled', color: 'bg-red-100 text-red-800' }
    };
    const statusInfo = statusMap[status] || { label: 'Unknown', color: 'bg-gray-100 text-gray-800' };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusInfo.color}`}>
        {statusInfo.label}
      </span>
    );
  };

  const [requestors, setRequestors] = useState([]);
  const fetchRequestors = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/Requestor`);
      const data = await res.json();
      setRequestors(data);
    } catch (error) { console.error(error); }
  };

  const stats = {
    total: prHeaders.length,
    approved: prHeaders.filter(pr => pr.status === 2).length,
    pending: prHeaders.filter(pr => pr.status === 1).length,
    filtered: filteredPRs.length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-blue-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mx-auto"></div>
          <p className="mt-4 text-blue-700 font-semibold">Loading PR Reports...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-blue-100">
        <div className="bg-white border border-red-200 rounded-2xl p-8 max-w-md shadow-2xl">
          <AlertCircle className="h-12 w-12 text-red-600 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-red-900 text-center mb-3">Error Loading Data</h3>
          <p className="text-red-700 text-center mb-4">{error}</p>
          <button onClick={fetchPRHeaders} className="w-full bg-blue-600 text-white py-3 rounded-xl hover:bg-blue-700 transition-colors font-semibold shadow-lg">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const logoSrc = getLogoSrc();

  return (
    <div className="min-h-screen from-blue-50 to-blue-100 p-6">
      <div className="max-w-8xl mx-auto">

        {/* ── HEADER ── */}
        <div className="bg-white rounded-2xl p-8 shadow-lg mb-8 border border-blue-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-600 rounded-xl">
                <FileText className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-slate-800">Purchase Requisition Reports</h1>
                <p className="text-slate-600 mt-1">View and manage all purchase requisitions</p>
              </div>
            </div>
            {/* EXPORT TO EXCEL BUTTON */}
            <button
              onClick={handleExportToExcel}
              disabled={filteredPRs.length === 0}
              className="flex items-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-xl font-semibold transition-all shadow-md hover:shadow-lg"
            >
              <Download className="w-5 h-5" />
              Export to Excel ({filteredPRs.length})
            </button>
          </div>
        </div>

        {/* ── STATS ── */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          {[
            { label: 'Total PRs', value: stats.total, color: 'text-slate-800', bg: 'bg-blue-100', icon: <FileText className="h-7 w-7 text-blue-600" /> },
            { label: 'Approved', value: stats.approved, color: 'text-green-600', bg: 'bg-green-100', icon: <TrendingUp className="h-7 w-7 text-green-600" /> },
            { label: 'Pending', value: stats.pending, color: 'text-yellow-600', bg: 'bg-yellow-100', icon: <Clock className="h-7 w-7 text-yellow-600" /> },
            { label: 'Filtered', value: stats.filtered, color: 'text-blue-600', bg: 'bg-blue-100', icon: <Package className="h-7 w-7 text-blue-600" /> },
          ].map(({ label, value, color, bg, icon }) => (
            <div key={label} className="bg-white rounded-xl shadow-md p-6 border border-blue-200 hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 mb-1 font-medium uppercase">{label}</p>
                  <p className={`text-3xl font-bold ${color}`}>{value}</p>
                </div>
                <div className={`p-3 ${bg} rounded-xl`}>{icon}</div>
              </div>
            </div>
          ))}
        </div>

        {/* ── SEARCH & FILTER ── */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8 border border-blue-200">
          <div className="flex gap-4 items-center mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search by PR number or remarks..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-5 py-3 rounded-xl font-medium transition-all ${showFilters ? 'bg-blue-600 text-white shadow-lg' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
            >
              <Filter className="w-5 h-5" />
              Filters
            </button>
          </div>
          {showFilters && (
            <div className="pt-4 border-t border-slate-200">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium"
              >
                <option value="all">All Status</option>
                <option value="0">Draft</option>
                <option value="1">Pending</option>
                <option value="2">Approved</option>
                <option value="3">Rejected</option>
                <option value="4">Cancelled</option>
              </select>
            </div>
          )}
        </div>

        {/* ── TABLE ── */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden border border-blue-200">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                <tr>
                  <th onClick={() => handleSort('prNo')} className="px-6 py-4 text-left text-xs font-bold uppercase cursor-pointer hover:bg-blue-800 transition-colors">
                    <div className="flex items-center gap-2">PR Number {getSortIcon('prNo')}</div>
                  </th>
                  <th onClick={() => handleSort('requestor')} className="px-6 py-4 text-left text-xs font-bold uppercase cursor-pointer hover:bg-blue-800 transition-colors">
                    <div className="flex items-center gap-2">Requestor {getSortIcon('requestor')}</div>
                  </th>
                  <th onClick={() => handleSort('chargedToDept')} className="px-6 py-4 text-left text-xs font-bold uppercase cursor-pointer hover:bg-blue-800 transition-colors">
                    <div className="flex items-center gap-2">Department {getSortIcon('chargedToDept')}</div>
                  </th>
                  <th onClick={() => handleSort('creationDate')} className="px-6 py-4 text-left text-xs font-bold uppercase cursor-pointer hover:bg-blue-800 transition-colors">
                    <div className="flex items-center gap-2">Created Date {getSortIcon('creationDate')}</div>
                  </th>
                  <th onClick={() => handleSort('dateNeeded')} className="px-6 py-4 text-left text-xs font-bold uppercase cursor-pointer hover:bg-blue-800 transition-colors">
                    <div className="flex items-center gap-2">Date Needed {getSortIcon('dateNeeded')}</div>
                  </th>
                  <th onClick={() => handleSort('status')} className="px-6 py-4 text-left text-xs font-bold uppercase cursor-pointer hover:bg-blue-800 transition-colors">
                    <div className="flex items-center gap-2">Status {getSortIcon('status')}</div>
                  </th>
                  <th className="px-6 py-4 text-center text-xs font-bold uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {filteredPRs.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center">
                      <Package className="w-16 h-16 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-600 font-medium">No purchase requisitions found</p>
                    </td>
                  </tr>
                ) : (
                  filteredPRs.map((pr, idx) => (
                    <tr key={pr.id} className={`hover:bg-slate-50 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}>
                      <td className="px-6 py-4"><div className="font-bold text-blue-600">{pr.prNo}</div></td>
                      <td className="px-6 py-4 text-sm text-slate-700 font-medium">{pr.requestor || '—'}</td>
                      <td className="px-6 py-4 text-sm text-slate-700 font-medium">{departments[pr.chargedToDept] || '—'}</td>
                      <td className="px-6 py-4 text-sm text-slate-700 font-medium">{formatDate(pr.creationDate)}</td>
                      <td className="px-6 py-4 text-sm text-slate-700 font-medium">{formatDate(pr.dateNeeded)}</td>
                      <td className="px-6 py-4">{getStatusBadge(pr.status)}</td>
                      <td className="px-6 py-4 text-center">
                        <button onClick={() => handleViewDetails(pr)} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all shadow-md hover:shadow-lg font-medium">
                          <Eye className="h-4 w-4" />View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ── MODAL ── */}
      {showModal && selectedPR && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-7xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white z-20 flex justify-end p-4 border-b">
              <button onClick={closeModal} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 font-sans space-y-6">
              <div className="no-print flex justify-end gap-3 mb-4">
                <button onClick={() => window.print()} className="flex items-center gap-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white px-6 py-3 rounded-xl font-bold hover:shadow-lg transition-all">
                  <Printer size={20} />Print
                </button>
                <button onClick={handleExportToPDF} className="flex items-center gap-2 bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-3 rounded-xl font-bold hover:shadow-lg transition-all">
                  <Download size={20} />PDF
                </button>
              </div>

              <div id="pr-pdf-content" className="bg-white p-8">
                {/* HEADER */}
                <div className="border-b-2 border-gray-400 pb-6 mb-6">
                  <div className="text-center mb-4 relative">
                    <div className="relative inline-block group">
                      {logoSrc ? (
                        <div className="flex justify-center mb-5">
                          <img src={logoSrc} alt="Company Logo" className="h-32 w-auto object-contain max-w-sm" onError={(e) => { e.target.style.display = 'none'; }} />
                          <div className="no-print absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                            <button onClick={() => setShowLogoUploader(true)} className="p-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-all" title="Change Logo">
                              <Upload size={16} />
                            </button>
                            {customLogo && (
                              <button onClick={handleResetLogo} className="p-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-all" title="Reset to Default">
                                <RotateCcw size={16} />
                              </button>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div onClick={() => setShowLogoUploader(true)} className="no-print flex justify-center mb-3 cursor-pointer">
                          <div className="h-20 w-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center hover:border-blue-500 hover:bg-blue-50 transition-all">
                            <div className="text-center">
                              <Upload size={24} className="mx-auto text-gray-400 mb-1" />
                              <p className="text-xs text-gray-500">Upload Logo</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    {websiteInfo?.websiteSlogan && <p className="text-sm text-gray-600 italic mb-2">{websiteInfo.websiteSlogan}</p>}
                    {websiteInfo?.websiteAddress && <p className="text-sm text-gray-700 mb-1">{websiteInfo.websiteAddress}</p>}
                    {websiteInfo?.websiteContact && <p className="text-sm text-gray-700 mb-1">{websiteInfo.websiteContact}</p>}
                  </div>
                  <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-300">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-800">Purchase Requisition</h2>
                      <p className="text-base text-gray-600">PR No: <span className="font-semibold">{selectedPR.prNo || "—"}</span></p>
                    </div>
                    <div className="text-right text-base">
                      <div className="text-gray-600">Date Printed:</div>
                      <div className="font-semibold">{formatDate(new Date())}</div>
                    </div>
                  </div>
                </div>

                {/* REQUEST INFO */}
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-3 border-b border-gray-300 pb-2">
                    <h3 className="text-xl font-semibold text-gray-800">Request Information</h3>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-500">Control Number:</span>
                      <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-lg font-mono text-sm font-semibold">
                        {selectedPR.controlNumber || 'N/A'}
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-base">
                    {[
                      ["Requestor", selectedPR.requestor || "—"],
                      ["Department", departments[selectedPR.chargedToDept] || "—"],
                      ["Date Needed", formatDate(selectedPR.dateNeeded)],
                      ["Date Submitted", formatDate(selectedPR.dateSubmittedForApproval)],
                      ["Priority", selectedPR.priority || "—"],
                    ].map(([label, value]) => (
                      <div key={label} className="flex gap-2">
                        <span className="font-semibold text-gray-700 min-w-[160px]">{label}:</span>
                        <span className="text-gray-600 break-words">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* REMARKS */}
                {selectedPR.remarks && (
                  <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-md text-amber-900 text-base mb-6">
                    <strong className="block mb-1">Remarks:</strong>
                    <span>{selectedPR.remarks}</span>
                  </div>
                )}

                {/* ITEMS TABLE */}
                <div className="mb-6">
                  <h3 className="text-xl font-semibold text-gray-800 mb-3 border-b border-gray-300 pb-2">
                    Items Requested ({prDetails.length})
                  </h3>
                  <div className="overflow-x-auto shadow-md rounded-lg">
                    <table className="w-full border-collapse border border-gray-300 text-base">
                      <thead className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                        <tr>
                          {["#", "Item Description", "Specification", "Qty", "UOM", "Unit Price", "Cost Estimate", "Material/Finish"].map((header) => (
                            <th key={header} className="border border-blue-500 px-3 py-3 text-left font-semibold">{header}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {prDetails.length > 0 ? (
                          prDetails.map((d, idx) => (
                            <tr key={d.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                              <td className="border border-gray-300 px-3 py-2 text-center font-medium text-gray-600">{idx + 1}</td>
                              <td className="border border-gray-300 px-3 py-2 break-words">{d.itemDescription}</td>
                              <td className="border border-gray-300 px-3 py-2 break-words">{d.specification || "—"}</td>
                              <td className="border border-gray-300 px-3 py-2 text-center font-semibold">{d.qty}</td>
                              <td className="border border-gray-300 px-3 py-2 text-center">{d.uom}</td>
                              <td className="border border-gray-300 px-3 py-2 text-right whitespace-nowrap">₱{d.unitPrice?.toFixed(2)}</td>
                              <td className="border border-gray-300 px-3 py-2 text-right font-semibold text-green-700 whitespace-nowrap">₱{d.costEstimate?.toFixed(2)}</td>
                              <td className="border border-gray-300 px-3 py-2 break-words">{d.materialFinish || "—"}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="8" className="px-3 py-8 text-center text-gray-500">
                              <div className="flex flex-col items-center gap-2">
                                <span className="text-2xl">📋</span>
                                <span>No items found</span>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                      {prDetails.length > 0 && (
                        <tfoot className="bg-gray-100">
                          <tr>
                            <td colSpan="6" className="border border-gray-300 px-3 py-3 text-right font-bold text-gray-800 text-base">GRAND TOTAL:</td>
                            <td className="border border-gray-300 px-3 py-3 text-right font-bold text-green-700 text-lg whitespace-nowrap">
                              ₱{prDetails.reduce((sum, d) => sum + (d.costEstimate || 0), 0).toFixed(2)}
                            </td>
                            <td className="border border-gray-300 px-3 py-3"></td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                </div>

                {/* ATTACHMENTS */}
                {prAttachments.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-xl font-semibold text-gray-800 mb-3 border-b border-gray-300 pb-2">
                      Attachments ({prAttachments.length})
                    </h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <ul className="space-y-2">
                        {prAttachments.map(att => (
                          <li key={att.id} className="flex justify-between items-center bg-white p-3 rounded-md shadow-sm border border-gray-200">
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <span className="text-2xl">📎</span>
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-gray-800 break-words">{att.filename}</div>
                                <div className="text-sm text-gray-500">{att.contentType}{att.description && ` • ${att.description}`}</div>
                              </div>
                            </div>
                            <button onClick={() => alert('Download attachment')} className="no-print ml-3 px-4 py-2 bg-blue-500 text-white rounded-md text-sm font-medium hover:bg-blue-600 transition-colors flex items-center gap-2 whitespace-nowrap">
                              <span>📥</span>Download
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {/* TOP SIGNATURES — REQUESTOR / ENDORSEMENT / ACKNOWLEDGEMENT */}
                <div className="mt-6 pt-4 border-t border-gray-400">
                  <div className="grid grid-cols-3 gap-10 text-center">
                    {/* REQUESTOR */}
                    {(() => {
                      const requestorName = selectedPR?.requestor || `User ${selectedPR?.createdBy || "-"}`;
                      const requestorData = requestors.find(r => r.name === requestorName);
                      const signatureUrl = requestorData?.signature ? `${API_BASE_URL}api/Requestor/${requestorData.id}/signature` : null;
                      return (
                        <div className="flex flex-col items-center">
                          <div className="h-14 flex items-center justify-center">
                            {signatureUrl && <img src={signatureUrl} alt="Requestor signature" className="h-12 max-w-[140px] object-contain" crossOrigin="anonymous" onError={(e) => (e.currentTarget.style.display = "none")} />}
                          </div>
                          <div className="text-sm font-semibold leading-tight">{requestorName}</div>
                          <div className="w-full border-b border-gray-500 my-1" />
                          <div className="text-xs text-gray-500 leading-tight">
                            {selectedPR?.creationDate ? new Date(selectedPR.creationDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : ""}
                          </div>
                        </div>
                      );
                    })()}

                    {/* ENDORSEMENT (Level 2) */}
                    {(() => {
                      const endorsement = secondApprovals.find(a => a.approvalLevel === 2);
                      return (
                        <div className="flex flex-col items-center">
                          <div className="h-14 flex items-center justify-center">
                            {endorsement?.actualApproverId && <img src={`${API_BASE_URL}api/Users/${endorsement.actualApproverId}/digitalsign`} alt="Endorsement signature" className="h-12 max-w-[140px] object-contain" crossOrigin="anonymous" onError={(e) => (e.currentTarget.style.display = "none")} />}
                          </div>
                          <div className="text-sm font-semibold leading-tight">{endorsement?.actualApproverName || "-"}</div>
                          <div className="w-full border-b border-gray-500 my-1" />
                          <div className="text-xs text-gray-500 leading-tight">
                            {endorsement?.responseDate ? new Date(endorsement.responseDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : ""}
                          </div>
                        </div>
                      );
                    })()}

                    {/* ACKNOWLEDGEMENT (Level 3) */}
                    {(() => {
                      const acknowledgement = secondApprovals.find(a => a.approvalLevel === 3);
                      return (
                        <div className="flex flex-col items-center">
                          <div className="h-14 flex items-center justify-center">
                            {acknowledgement?.actualApproverId && <img src={`${API_BASE_URL}api/Users/${acknowledgement.actualApproverId}/digitalsign`} alt="Acknowledgement signature" className="h-12 max-w-[140px] object-contain" crossOrigin="anonymous" onError={(e) => (e.currentTarget.style.display = "none")} />}
                          </div>
                          <div className="text-sm font-semibold leading-tight">{acknowledgement?.actualApproverName || "-"}</div>
                          <div className="w-full border-b border-gray-500 my-1" />
                          <div className="text-xs text-gray-500 leading-tight">
                            {acknowledgement?.responseDate ? new Date(acknowledgement.responseDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : ""}
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  <div className="grid grid-cols-3 gap-10 text-center text-xs text-gray-600 mt-2">
                    <div>Signature over Printed Name</div>
                    <div>Signature over Printed Name</div>
                    <div>Signature over Printed Name</div>
                  </div>
                  <div className="grid grid-cols-3 gap-10 text-center text-sm font-bold mt-1">
                    <div className="uppercase">REQUESTOR</div>
                    <div className="uppercase">ENDORSEMENT</div>
                    <div className="uppercase">ACKNOWLEDGEMENT</div>
                  </div>
                </div>

                {/* BOTTOM APPROVALS */}
                <div className="mt-8 pt-6 border-t-2 border-gray-400">
                  <h3 className="text-xl font-semibold text-gray-800 mb-3 border-b border-gray-300 pb-2">Approvals</h3>
                  <div className="grid grid-cols-3 gap-8 text-center text-base">
                    {approverPositions.map((approver) => {
                      const approved = getApprovedUser(approver.approverUserLevelId);
                      return (
                        <div key={approver.id} className="flex flex-col items-center">
                          <div className="h-20 flex items-center justify-center mb-1">
                            {approved?.signatureUrl && (
                              <img src={approved.signatureUrl} alt={`${approved.name} signature`} className="h-16 object-contain" crossOrigin="anonymous" onError={(e) => { e.target.style.display = "none"; }} />
                            )}
                          </div>
                          <div className="font-semibold">{approved?.name || "—"}</div>
                          <div className="w-full border-b border-gray-400 my-1" />
                          <div className="text-sm font-bold mt-1">{approver.approverPosition}</div>
                          <div className="text-xs text-gray-600 mt-1">Signature over Printed Name</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* LOGO UPLOADER MODAL */}
              {showLogoUploader && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-xl font-bold text-gray-800">Upload Custom Logo</h3>
                      <button onClick={() => setShowLogoUploader(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                        <X size={20} />
                      </button>
                    </div>
                    <div className="space-y-4">
                      <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-blue-500 hover:bg-blue-50 transition-all cursor-pointer" onClick={() => document.getElementById('logoInput').click()}>
                        <Upload size={48} className="mx-auto text-gray-400 mb-3" />
                        <p className="text-gray-700 font-medium mb-1">Click to upload logo</p>
                        <p className="text-sm text-gray-500">PNG, JPG up to 2MB</p>
                      </div>
                      <input type="file" id="logoInput" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                      {customLogo && (
                        <button onClick={handleResetLogo} className="w-full py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-colors">
                          Reset to Default
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <style jsx>{`
                @media print {
                  .no-print { display: none !important; }
                  body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
                  @page { margin: 1cm; size: A4; }
                }
              `}</style>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PR_Reports;